package org.dronda.app.web.ui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.getValue
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import kotlinx.browser.window
import kotlinx.serialization.Serializable
import org.dronda.app.web.Loader
import org.dronda.app.web.MainModule
import org.dronda.app.web.rememberViewModel
import org.dronda.app.web.ui.eventadmin.EventAdminScreen
import org.dronda.app.web.ui.eventcreate.CreateEventScreen
import org.dronda.app.web.ui.eventdetail.EventDetailScreen
import org.dronda.app.web.ui.events.EventsScreen
import org.dronda.app.web.ui.locationconfig.LocationConfigScreen
import org.dronda.app.web.ui.login.LoginScreen
import org.dronda.app.web.ui.nav.NavHost
import org.dronda.app.web.ui.nav.Router
import org.dronda.app.web.ui.notfound.NotFoundScreen
import org.dronda.app.web.ui.scaffolding.Scaffolding
import org.dronda.app.web.ui.wishlist.WishlistScreen
import org.w3c.dom.events.Event
import kotlin.uuid.ExperimentalUuidApi

@OptIn(ExperimentalUuidApi::class)
@Composable
fun MainScreen(mainScreenViewModel: MainScreenViewModel, mainModule: MainModule) {
    val uiState by mainScreenViewModel.state.collectAsState()

    // Create shared route state (outside Scaffolding to use in callback)
    val initialRoute = remember { Router.fromCurrentUrl() }
    val currentRouteState = remember { mutableStateOf<Route>(initialRoute) }
    val router = remember { Router(currentRouteState) }

    Scaffolding(
        onLogoClick = if (uiState.isLoggedIn) {
            { router.navigate(Route.Events) }
        } else null,
        onSettingsClick = if (uiState.isLoggedIn) {
            { router.navigate(Route.LocationConfig) }
        } else null
    ) {
        if (uiState.loading) {
            Column(
                modifier = Modifier.fillMaxSize(),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Loader()
            }
            return@Scaffolding
        }

        if (uiState.isLoggedIn) {
            // Set up browser back/forward button handling
            DisposableEffect(Unit) {
                val listener: (Event) -> Unit = {
                    router.handlePopState(window.location.pathname)
                }
                window.addEventListener("popstate", listener)

                onDispose {
                    window.removeEventListener("popstate", listener)
                }
            }

            // Use NavHost with the shared route state
            NavHost(
                modifier = Modifier.fillMaxSize(),
                currentRouteState
            ) {
                composable<Route.LocationConfig> {
                    val locationConfigViewModel = rememberViewModel(mainModule.locationConfigViewModelFactory)
                    LocationConfigScreen(locationConfigViewModel)
                }
                composable<Route.Events> {
                    val eventsViewModel = rememberViewModel(mainModule.eventsViewModelFactory)
                    EventsScreen(
                        viewModel = eventsViewModel,
                        onEventClick = { eventId -> router.navigate(Route.EventDetail(eventId)) },
                        onCreateClick = { router.navigate(Route.EventCreate) }
                    )
                }
                composable<Route.EventCreate> {
                    val creatorEmail = mainScreenViewModel.userRepository.loggedInState?.username ?: ""
                    val createEventViewModel = remember(creatorEmail) {
                        mainModule.createEventViewModelFactory(creatorEmail)
                    }
                    CreateEventScreen(
                        viewModel = createEventViewModel,
                        onCancel = { router.navigate(Route.Events) },
                        onCreated = { eventId -> router.navigate(Route.EventDetail(eventId), replace = true) }
                    )
                }
                composable<Route.EventDetail> {
                    val eventDetailViewModel = remember(eventId) { mainModule.eventDetailViewModelFactory(eventId) }
                    EventDetailScreen(
                        viewModel = eventDetailViewModel,
                        onWishlistClick = { wishlistId ->
                            router.navigate(Route.Wishlist(wishlistId, eventId))
                        },
                        onAdminClick = { router.navigate(Route.EventAdmin(eventId)) }
                    )
                }
                composable<Route.EventAdmin> {
                    val eventAdminViewModel = remember(eventId) { mainModule.eventAdminViewModelFactory(eventId) }
                    EventAdminScreen(
                        viewModel = eventAdminViewModel,
                        onBackClick = { router.navigate(Route.EventDetail(eventId)) }
                    )
                }
                composable<Route.Wishlist> {
                    val currentUserId = mainScreenViewModel.userRepository.loggedInState?.userUuid?.toString() ?: ""
                    val wishlistViewModel = remember(wishlistId, currentUserId) {
                        mainModule.wishlistViewModelFactory(wishlistId, currentUserId)
                    }
                    WishlistScreen(
                        viewModel = wishlistViewModel,
                        onBackClick = { router.navigate(Route.EventDetail(eventId)) }
                    )
                }
                composable<Route.Login> {
                    val loginViewModel = rememberViewModel(mainModule.loginViewModelFactory)
                    LoginScreen(loginViewModel)
                }
                composable<Route.NotFound> {
                    NotFoundScreen(
                        onNavigateHome = { router.navigate(Route.Events) }
                    )
                }
            }
        } else {
            val loginViewModel = rememberViewModel(mainModule.loginViewModelFactory)
            LoginScreen(loginViewModel)
        }
    }
}


sealed interface Route {
    @Serializable
    object Login : Route
    @Serializable
    object LocationConfig : Route
    @Serializable
    object Events : Route
    @Serializable
    object EventCreate : Route
    @Serializable
    data class EventDetail(val eventId: String) : Route
    @Serializable
    data class EventAdmin(val eventId: String) : Route
    @Serializable
    data class Wishlist(val wishlistId: String, val eventId: String) : Route
    @Serializable
    object NotFound : Route
}
